// =============================================================================
// features/conversations/statusConfig.ts — Configuração canônica de status (F16 UI).
//
// Fonte única de verdade para rótulos e cores dos 4 status de conversa.
// Importado por ChatListFilters (abas) e ContactPanel (controle de status).
//
// Cores: open/pending/snoozed usam valores semânticos compatíveis com light+dark;
// resolved usa var(--brand-azul) (token do DS).
//
// NUNCA duplicar essa paleta em componentes — sempre importar daqui.
//
// -----------------------------------------------------------------------------
// CICLO DE VIDA CANÔNICO (regra de negócio — decidida pelo BdP em 2026-08-03)
// -----------------------------------------------------------------------------
//
//   aberta  ──IA qualifica──▶  pendente  ──atendente conclui──▶  resolvida
//
//   open     A IA está no PRIMEIRO ATENDIMENTO. Toda conversa nasce aqui.
//            Não é fila de humano — é a IA trabalhando.
//   pending  A IA terminou e passou adiante (handoff). É A FILA DO ATENDENTE:
//            o único status que representa trabalho humano pendente. Por isso
//            é o filtro padrão da inbox.
//   resolved O atendente concluiu. Normalmente após o contrato fechado — daí
//            em diante é só cobrança de boleto.
//   snoozed  Adiada manualmente.
//
// Quem escreve cada transição:
//   → pending   apenas triggerLivechatHandoff (api/modules/livechat/ai-handoff.ts)
//   → resolved  apenas ação manual do atendente (resolveConversation)
//   → open      só na REABERTURA: mensagem nova do cidadão em conversa
//               `resolved` volta para `open` (a IA reassume o primeiro
//               atendimento). `pending`/`snoozed` são preservados de propósito.
//
// ⚠️ Atribuir atendente NÃO muda o status — assignConversation só grava
//    assigned_user_id. Uma conversa em atendimento humano continua `pending`.
// =============================================================================

import type { ConversationStatus } from './types';

// ---------------------------------------------------------------------------
// Tipos públicos
// ---------------------------------------------------------------------------

export interface StatusMeta {
  /** Rótulo em português exibido na UI */
  readonly label: string;
  /**
   * Cor canônica do status.
   * open/pending/snoozed: hex contextual (sem equivalente no DS de marca).
   * resolved: var(--brand-azul) (token DS).
   */
  readonly color: string;
}

// ---------------------------------------------------------------------------
// Mapa de configuração
// ---------------------------------------------------------------------------

export const STATUS_CONFIG: Record<ConversationStatus, StatusMeta> = {
  open: { label: 'Aberta', color: '#16a34a' },
  pending: { label: 'Pendente', color: '#d97706' },
  resolved: { label: 'Resolvida', color: 'var(--brand-azul)' },
  snoozed: { label: 'Adiada', color: '#7c3aed' },
} as const;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Retorna o rótulo de um status, com fallback seguro. */
export function getStatusLabel(status: ConversationStatus): string {
  return STATUS_CONFIG[status]?.label ?? status;
}

/** Retorna a cor de um status, com fallback seguro. */
export function getStatusColor(status: ConversationStatus): string {
  return STATUS_CONFIG[status]?.color ?? 'var(--text-3)';
}
